import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { API_BASE } from '@/lib/api-base'

function RegisterPage() {
  const [form, setForm] = useState({
    granja_nome: "",
    tipo_producao: "postura",
    nome: "",
    email: "",
    senha: "",
  })
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const { login, user, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!authLoading && user) {
      void navigate({ to: '/dashboard', replace: true })
    }
  }, [authLoading, user, navigate])

  // Use environment variable like other pages (Fix for issue #1)
  const apiBase = API_BASE
  const validateForm = () => {
    if (!form.granja_nome.trim()) {
      return "Nome da granja e obrigatorio"
    }
    if (!form.nome.trim()) {
      return "Seu nome e obrigatorio"
    }
    if (!form.email.trim()) {
      return "Email e obrigatorio"
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(form.email)) {
      return "Email invalido"
    }
    if (!form.senha) {
      return "Senha e obrigatoria"
    }
    if (form.senha.length < 6) {
      return "Senha deve ter pelo menos 6 caracteres"
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    // Client-side validation (Fix for issue #5)
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)

    try {
      const res = await fetch(`${apiBase}/api/auth/register`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      })

      if (res.ok) {
        const ok = await login()
        if (ok) {
          void navigate({ to: "/dashboard" })
        } else {
          setError("Conta criada, mas nao foi possivel iniciar a sessao.")
        }
      } else {
        // Try to parse error response safely
        let errorMessage = "Erro ao registrar"
        try {
          const data = await res.json()
          errorMessage = data.detail || data.message || errorMessage
        } catch {
          // If response is not JSON, use generic message
          errorMessage = res.status === 409 
            ? "Email ja cadastrado" 
            : "Erro ao registrar. Tente novamente."
        }
        setError(errorMessage)
      }
    } catch {
      setError("Erro de conexao. Verifique sua internet.")
    }

    setLoading(false)
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">Registrar Granja</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="granja_nome">Nome da Granja</Label>
              <Input
                id="granja_nome"
                value={form.granja_nome}
                onChange={(e) => setForm({ ...form, granja_nome: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tipo_producao">Tipo de Producao</Label>
              <Select
                value={form.tipo_producao}
                onValueChange={(v) => setForm({ ...form, tipo_producao: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="postura">Postura</SelectItem>
                  <SelectItem value="corte">Corte</SelectItem>
                  <SelectItem value="mista">Mista</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nome">Seu Nome</Label>
              <Input
                id="nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="senha">Senha</Label>
              <Input
                id="senha"
                type="password"
                value={form.senha}
                onChange={(e) => setForm({ ...form, senha: e.target.value })}
                required
                minLength={6}
              />
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Registrando..." : "Registrar"}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-4">
            Ja tem conta?{" "}
            <Button variant="link" onClick={() => void navigate({ to: "/login" })}>
              Entrar
            </Button>
          </p>
        </CardContent>
      </Card>
    </main>
  )
}

export const Route = createFileRoute('/register')({
  component: RegisterPage,
})
